import * as Sentry from '@sentry/react';
import type { AxiosError } from 'axios';
import { toast } from 'sonner';

type ValidationIssue = {
  message?: string;
  instancePath?: string;
};

type ApiErrorResponse = {
  message?: string;
  error?: string;
  statusCode?: number;
  details?: {
    issues?: ValidationIssue[];
    // Handle nested errors from proxied responses
    body?: {
      message?: string;
      details?: {
        issues?: ValidationIssue[];
      };
    };
  };
};

/**
 * Gets a user-friendly message for HTTP status codes
 */
function getHttpStatusMessage(status: number | undefined): string | null {
  if (!status) {
    return null;
  }

  if (status === 401) {
    return 'Authentication expired. Please log in again.';
  }
  if (status === 403) {
    return 'You do not have permission to perform this action.';
  }
  if (status === 404) {
    return 'The requested resource was not found.';
  }
  if (status >= 500) {
    return 'A server error occurred. Please try again later.';
  }

  return null;
}

/**
 * Extracts validation issue messages from API response
 */
function getValidationIssueMessage(data: ApiErrorResponse | undefined): string | null {
  const issues = data?.details?.issues ?? data?.details?.body?.details?.issues;

  if (!issues || issues.length === 0) {
    return null;
  }

  const messages = issues.map((issue) => issue.message).filter(Boolean);

  if (messages.length === 0) {
    return null;
  }

  return messages.join('; ');
}

/**
 * Derives a message from a parsed/typed Axios response body, if one is present.
 * Returns null when the body carries no usable message (caller then falls back
 * to the status-code message).
 */
function getMessageFromResponseData(responseData: ApiErrorResponse | undefined): string | null {
  const validationMessage = getValidationIssueMessage(responseData);
  if (validationMessage) {
    return validationMessage;
  }

  const responseMessage = responseData?.details?.body?.message || responseData?.message || responseData?.error;
  if (responseMessage) {
    return responseMessage;
  }

  return null;
}

/**
 * Maps the non-body parts of an Axios error (status code, transport code) to a
 * message. Used as the fallback when the response body yields nothing.
 */
function getAxiosFallbackMessage(axiosError: AxiosError): string {
  const statusMessage = getHttpStatusMessage(axiosError.response?.status);
  if (statusMessage) {
    return statusMessage;
  }

  if (axiosError.code === 'ERR_NETWORK') {
    return 'Network error. Please check your connection.';
  }
  if (axiosError.code === 'ECONNABORTED') {
    return 'Request timed out. Please try again.';
  }

  return 'An unexpected error occurred. Please try again.';
}

/**
 * Extracts error message from Axios error
 */
function getAxiosErrorMessage(axiosError: AxiosError<ApiErrorResponse>): string {
  const bodyMessage = getMessageFromResponseData(axiosError.response?.data);
  if (bodyMessage) {
    return bodyMessage;
  }

  return getAxiosFallbackMessage(axiosError);
}

/**
 * Type guard: is this error an Axios error whose response body is a Blob?
 *
 * Blob-typed bodies happen when a request uses `responseType: 'blob'` (e.g. file
 * downloads). On an error the body is never JSON-parsed by axios, so the usual
 * `response.data.message` extraction sees nothing and we fall through to the
 * generic status message — which is how a Cloud Armor WAF 403 ends up surfaced
 * as "You do not have permission to perform this action." Reading the blob as
 * text lets us recover the real message/status (KIP-63).
 */
function isAxiosBlobError(error: unknown): error is AxiosError<Blob> {
  return (
    error !== null &&
    typeof error === 'object' &&
    'isAxiosError' in error &&
    (error as AxiosError).response?.data instanceof Blob
  );
}

/**
 * Reads an error response Blob as text, parses it as JSON when possible, and
 * derives a user-friendly message. Falls back to the status-code message (and
 * finally the generic message) when the blob can't be read or parsed.
 */
async function getAxiosBlobErrorMessage(axiosError: AxiosError<Blob>): Promise<string> {
  const blob = axiosError.response?.data;

  if (blob instanceof Blob) {
    try {
      const text = await blob.text();

      if (text.trim().length > 0) {
        try {
          const parsed: unknown = JSON.parse(text);
          if (parsed !== null && typeof parsed === 'object') {
            const bodyMessage = getMessageFromResponseData(parsed as ApiErrorResponse);
            if (bodyMessage) {
              return bodyMessage;
            }
          }
        } catch {
          // Not JSON — fall back to the status message below rather than dump
          // raw HTML (e.g. a Cloud Armor 403 error page) into the toast.
        }
      }
    } catch {
      // Blob unreadable (e.g. already consumed) — fall back to status message.
    }
  }

  return getAxiosFallbackMessage(axiosError);
}

/**
 * Extracts a user-friendly error message from various error types
 */
export function getErrorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'isAxiosError' in error) {
    return getAxiosErrorMessage(error as AxiosError<ApiErrorResponse>);
  }

  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === 'string') {
    return error;
  }

  return 'An unexpected error occurred. Please try again.';
}

function isAxiosErrorLike(error: unknown): boolean {
  return error !== null && typeof error === 'object' && 'isAxiosError' in error;
}

/**
 * Displays an error toast with a user-friendly message and sends the error to Sentry.
 *
 * Axios errors are captured by the axios response interceptor in `lib/api.ts`, so we
 * skip them here to avoid double-reporting. Non-axios errors (validation throws,
 * synthesized AuthenticationError/RuntimeError, query parse failures, etc.) are still
 * captured here since nothing else sees them.
 */
export function showErrorToast(error: unknown, customMessage?: string): void {
  // When the response body is a Blob (e.g. a failed file download) the real
  // message lives inside the unparsed blob. Read it asynchronously so a
  // mis-surfaced WAF 403 shows its true cause instead of the generic
  // permission message (KIP-63). A custom message always wins, so we only
  // take the async path when none was provided.
  if (!customMessage && isAxiosBlobError(error)) {
    void getAxiosBlobErrorMessage(error).then((message) => {
      // Axios errors are reported by the response interceptor; don't double-report.
      toast.error(message);
    });
    return;
  }

  const message = customMessage || getErrorMessage(error);

  if (!isAxiosErrorLike(error)) {
    Sentry.captureException(error, {
      contexts: { toast: { displayedMessage: message } },
    });
  }

  toast.error(message);
}
